import { useEffect, useState } from 'react';
import { env } from '../config/env';
import { MAPS_REFERRER_ERROR } from '../utils/googleMapPins';

let loaderPromise: Promise<void> | null = null;
let authFailed = false;
const authListeners = new Set<(message: string) => void>();

function mapsApiReady(): boolean {
  const g = (window as Window & { google?: { maps?: { Map?: unknown; importLibrary?: unknown } } }).google;
  return Boolean(g?.maps?.Map || g?.maps?.importLibrary);
}

function notifyAuthFailure() {
  authFailed = true;
  authListeners.forEach((fn) => fn(MAPS_REFERRER_ERROR));
}

function installAuthFailureHook() {
  const w = window as Window & { gm_authFailure?: () => void };
  const previous = w.gm_authFailure;
  w.gm_authFailure = () => {
    previous?.();
    notifyAuthFailure();
  };
}

async function importCoreLibraries() {
  const g = (window as unknown as { google?: { maps?: { importLibrary?: (name: string) => Promise<unknown> } } }).google;
  if (!g?.maps?.importLibrary) return;
  await g.maps.importLibrary('maps');
  await g.maps.importLibrary('marker');
  await g.maps.importLibrary('geometry');
  await g.maps.importLibrary('geocoding');
}

function waitForMapsApi(timeoutMs = 15000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (mapsApiReady()) {
        resolve();
        return;
      }
      if (Date.now() - start > timeoutMs) {
        reject(new Error('Google Maps API timed out'));
        return;
      }
      setTimeout(tick, 40);
    };
    tick();
  });
}

/** Loads Google Maps JS API and waits until Map is usable. */
export function loadGoogleMapsScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (authFailed) return Promise.reject(new Error(MAPS_REFERRER_ERROR));
  if (mapsApiReady()) return importCoreLibraries().then(() => undefined);

  if (!env.googleMapsApiKey) {
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  if (!loaderPromise) {
    loaderPromise = new Promise<void>((resolve, reject) => {
      installAuthFailureHook();

      const fail = (err: Error) => {
        loaderPromise = null;
        reject(err);
      };

      const finish = async () => {
        try {
          if (authFailed) {
            fail(new Error(MAPS_REFERRER_ERROR));
            return;
          }
          await waitForMapsApi();
          await importCoreLibraries();
          if (authFailed) {
            fail(new Error(MAPS_REFERRER_ERROR));
            return;
          }
          resolve();
        } catch (err) {
          fail(err instanceof Error ? err : new Error('Google Maps failed to load'));
        }
      };

      const existing = document.getElementById('google-maps-script');
      if (existing) {
        void finish();
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${env.googleMapsApiKey}&v=weekly&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => { void finish(); };
      script.onerror = () => fail(new Error('Failed to load Google Maps'));
      document.head.appendChild(script);
    });
  }

  return loaderPromise;
}

export function useGoogleMaps() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!env.googleMapsApiKey) {
      setError('Add VITE_GOOGLE_MAPS_API_KEY to frontend/.env.local');
      return;
    }

    const onFail = (message: string) => {
      setReady(false);
      setError(message);
    };
    authListeners.add(onFail);
    if (authFailed) {
      setError(MAPS_REFERRER_ERROR);
      return () => { authListeners.delete(onFail); };
    }

    loadGoogleMapsScript()
      .then(() => {
        if (!authFailed) setReady(true);
      })
      .catch((err: Error) => setError(err.message));

    return () => { authListeners.delete(onFail); };
  }, []);

  return { ready, error, hasKey: Boolean(env.googleMapsApiKey) };
}

export function useGoogleMapsDrawing() {
  return useGoogleMaps();
}
